/**
 * Utility functions for tracking dismissed meeting toast notifications.
 *
 * Event IDs follow the format:
 *   CALENDAR_EVENT_ITEM=>{itemId}=>{ISO_timestamp}
 *   e.g. CALENDAR_EVENT_ITEM=>347514057223313382=>2026-03-09T03:30:00.000Z
 *
 * The third segment (ISO timestamp) is used to prune IDs older than 24 hours,
 * keeping the stored preference value compact.
 */

import _ from "lodash";
import { getDateMomentObject } from "Utils/DateTimeHelpers";

/**
 * Extract the ISO timestamp from the third segment of a calendar event ID.
 * Returns null if the ID doesn't have the expected format.
 */
export const getTimestampFromEventId = eventId => {
  const parts = _.split(eventId || "", "=>");
  return _.get(parts, "[2]", null);
};

/**
 * Remove event IDs whose embedded timestamp is older than 24 hours.
 * This prevents the preference value from growing indefinitely.
 */
export const pruneExpiredIds = ids => {
  const now = getDateMomentObject();
  const filtered = _.filter(ids, id => {
    const ts = getTimestampFromEventId(id);
    if (!ts) {
      return false;
    }
    const ageHours = now.diff(getDateMomentObject(ts), "hours");
    return ageHours < 24;
  });
  return filtered;
};

/**
 * Check if a given event ID exists in the dismissed list.
 * @param {object} dismissedData - The parsed preference value, e.g. { video_meeting: [...] }
 * @param {string} eventId - The calendar event ID to check
 */
export const isEventDismissed = (dismissedData, eventId) => {
  const videoMeetingIds = _.get(dismissedData, "video_meeting", []);
  return _.includes(videoMeetingIds, eventId);
};

/**
 * Add a new event ID to the dismissed list and prune expired entries.
 * Returns the updated preference value object ready to be saved.
 * @param {object} dismissedData - Current preference value, e.g. { video_meeting: [...] }
 * @param {string} eventId - The event ID to add
 */
export const addDismissedEventId = (dismissedData, eventId) => {
  const existing = _.get(dismissedData, "video_meeting", []);
  // Combine existing + new ID, then prune anything older than 24 hours
  const updated = pruneExpiredIds(_.concat(existing, eventId));
  return { ...dismissedData, video_meeting: updated };
};

/**
 * Determine whether a parent user is part of a meeting and should see its toast.
 *
 * Uses the same pattern as EventModal's Enhancer.js to distinguish
 * grade/course-level sharing from custom participants:
 *
 *   1. Check associatedEntities[0].__typename — if it's "Grade" or "Course",
 *      the meeting is shared with the whole grade/course. In that case,
 *      sharedWith containing "PARENT" means all parents are invited.
 *
 *   2. If associatedEntities is empty the meeting uses custom participants.
 *      We then look for FamilyMember entries in the users array whose id
 *      matches the logged-in parent's userId.
 *
 * @param {object} params
 * @param {Array<string>} params.sharedWith - User types invited, e.g. ["STAFF","STUDENT","PARENT"]
 * @param {Array<{id: string, __typename: string}>} params.associatedEntities - Grade/Course entities (empty for custom)
 * @param {Array<{id: string, __typename: string}>} params.users - Custom participant list
 * @param {string} params.parentUserId - The logged-in parent's user ID
 * @returns {boolean} true if the parent should see the meeting toast
 */
export const isParentPartOfMeeting = ({
  sharedWith,
  associatedEntities,
  users,
  parentUserId,
}) => {
  // Parent type must be in the sharedWith list at all
  if (!_.includes(sharedWith, "PARENT")) {
    return false;
  }

  // Determine if the meeting is shared at grade/course level or with custom
  // participants. Same check as EventModal — TYPENAME_MAPPING falls back to
  // CUSTOM when associatedEntities is empty or __typename is unrecognised.
  const typeName = _.get(associatedEntities, "0.__typename", "");
  const isGradeOrCourseLevel = typeName === "Grade" || typeName === "Course";

  if (isGradeOrCourseLevel) {
    // Grade/course-level share — "PARENT" in sharedWith means all parents
    // of students in that grade/course are invited.
    return true;
  }

  // Custom participants — verify this specific parent is in the users list.
  // FamilyMember __typename represents parent users in GraphQL.
  const parentUsers = _.filter(users, { __typename: "FamilyMember" });
  return _.some(parentUsers, { id: parentUserId });
};
